import React, { useEffect } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { CreditCardIcon, PrinterIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/LoadingSpinner';
import { publicInvoiceAPI } from '../../services/api';

interface PublicInvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unit_amount: number;
  amount: number;
}

interface PublicInvoiceSummary {
  id: string;
  invoice_number: string;
  status: string;
  currency: string;
  subtotal: number;
  tax_amount: number;
  discount_amount: number;
  total: number;
  amount_due: number;
  amount_paid: number;
  payment_url: string | null;
  payment_terms: string;
  issued_at: string | null;
  due_date: string | null;
  paid_at: string | null;
  description: string | null;
  memo: string | null;
  customer_details: {
    name?: string;
    email?: string;
    company_name?: string;
  };
  company_details: {
    name?: string;
    website?: string;
    phone?: string;
    address?: {
      line1?: string;
      line2?: string;
      city?: string;
      state?: string;
      postal_code?: string;
      country?: string;
    };
  };
  line_items: PublicInvoiceLineItem[];
}

function formatCurrency(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'USD').toUpperCase(),
  }).format((Number(cents) || 0) / 100);
}

function formatDate(value?: string | null): string {
  if (!value) return 'n/a';
  return new Date(value).toLocaleDateString();
}

function formatAddress(address?: PublicInvoiceSummary['company_details']['address']): string | null {
  if (!address) return null;

  const street = [address.line1, address.line2].filter(Boolean).join(', ');
  const locality = [address.city, address.state, address.postal_code].filter(Boolean).join(', ');
  const lines = [street, locality, address.country].filter(Boolean);

  return lines.length > 0 ? lines.join(' | ') : null;
}

export default function PublicInvoicePage() {
  const { token } = useParams<{ token: string }>();

  const invoiceQuery = useQuery(
    ['public-invoice', token],
    () => publicInvoiceAPI.getInvoice(token || ''),
    {
      enabled: Boolean(token),
      retry: false,
      select: (response) => response.data as PublicInvoiceSummary,
    },
  );

  const invoice = invoiceQuery.data;
  const companyName = invoice?.company_details?.name || 'Tenant Billing';

  useEffect(() => {
    if (invoice?.invoice_number) {
      document.title = `Invoice ${invoice.invoice_number} | ${companyName}`;
      return;
    }

    document.title = 'Invoice | Tenant Billing';
  }, [companyName, invoice?.invoice_number]);

  if (invoiceQuery.isLoading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16">
        <LoadingSpinner className="py-20" />
      </div>
    );
  }

  if (invoiceQuery.isError || !invoice) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        <div className="card p-8 text-center">
          <h1 className="text-2xl font-bold text-on-surface">Invoice unavailable</h1>
          <p className="mt-2 text-on-surface-variant">This invoice link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  const customerName = invoice.customer_details?.company_name || invoice.customer_details?.name || 'Customer';
  const companyAddress = formatAddress(invoice.company_details?.address);
  const canPayInvoice = Boolean(invoice.payment_url && invoice.amount_due > 0 && invoice.status !== 'paid');

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 print:max-w-none print:px-0 print:py-0">
      <div className="card-elevated print:shadow-none print:border-none">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-outline/60 px-6 py-6 print:px-0">
          <div>
            <h1 className="text-3xl font-bold text-on-surface">Invoice {invoice.invoice_number}</h1>
            <p className="mt-2 text-on-surface-variant">Status: {invoice.status}</p>
          </div>
          <div className="flex gap-2 print:hidden">
            {canPayInvoice && (
              <a
                href={invoice.payment_url || undefined}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-2"
              >
                <CreditCardIcon className="h-5 w-5" />
                Pay invoice
              </a>
            )}
            <button type="button" onClick={() => window.print()} className="btn-secondary inline-flex items-center gap-2">
              <PrinterIcon className="h-5 w-5" />
              Print
            </button>
          </div>
        </div>

        <div className="grid gap-6 px-6 py-6 sm:grid-cols-2 print:px-0">
          <div className="rounded-card border border-outline/70 bg-surface-container-low p-4">
            <p className="text-xs uppercase tracking-wide text-on-surface-variant">From</p>
            <p className="mt-1 text-sm font-semibold text-on-surface">{companyName}</p>
            {companyAddress && <p className="text-sm text-on-surface-variant">{companyAddress}</p>}
            {invoice.company_details?.website && <p className="text-sm text-on-surface-variant">{invoice.company_details.website}</p>}
            {invoice.company_details?.phone && <p className="text-sm text-on-surface-variant">{invoice.company_details.phone}</p>}
          </div>

          <div className="rounded-card border border-outline/70 bg-surface-container-low p-4">
            <p className="text-xs uppercase tracking-wide text-on-surface-variant">Bill to</p>
            <p className="mt-1 text-sm font-semibold text-on-surface">{customerName}</p>
            <p className="text-sm text-on-surface-variant">{invoice.customer_details?.email || 'No email on file'}</p>
          </div>
        </div>

        <div className="px-6 pb-4 print:px-0">
          <div className="overflow-hidden rounded-card border border-outline/70">
            <table className="min-w-full">
              <thead className="bg-surface-container-low">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-on-surface-variant">Description</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-on-surface-variant">Qty</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-on-surface-variant">Unit</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-on-surface-variant">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.line_items.map((item) => (
                  <tr key={item.id} className="border-t border-outline/40">
                    <td className="px-3 py-2 text-sm text-on-surface">{item.description || 'Line item'}</td>
                    <td className="px-3 py-2 text-right text-sm text-on-surface-variant">{item.quantity}</td>
                    <td className="px-3 py-2 text-right text-sm text-on-surface-variant">{formatCurrency(item.unit_amount, invoice.currency)}</td>
                    <td className="px-3 py-2 text-right text-sm font-medium text-on-surface">{formatCurrency(item.amount, invoice.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid gap-4 px-6 pb-6 sm:grid-cols-[1fr_auto] print:px-0">
          <div className="text-sm text-on-surface-variant">
            <p>Issued: {formatDate(invoice.issued_at)}</p>
            <p>Due: {formatDate(invoice.due_date)}</p>
            {invoice.description && <p className="mt-2">{invoice.description}</p>}
            {invoice.memo && <p className="mt-1">{invoice.memo}</p>}
          </div>

          <div className="rounded-card border border-outline/70 bg-surface-container-low p-4 min-w-[250px]">
            <div className="flex justify-between text-sm text-on-surface-variant">
              <span>Subtotal</span>
              <span>{formatCurrency(invoice.subtotal, invoice.currency)}</span>
            </div>
            <div className="mt-1 flex justify-between text-sm text-on-surface-variant">
              <span>Tax</span>
              <span>{formatCurrency(invoice.tax_amount, invoice.currency)}</span>
            </div>
            {invoice.discount_amount > 0 && (
              <div className="mt-1 flex justify-between text-sm text-on-surface-variant">
                <span>Discount</span>
                <span>-{formatCurrency(invoice.discount_amount, invoice.currency)}</span>
              </div>
            )}
            <div className="mt-2 border-t border-outline/60 pt-2 flex justify-between text-base font-semibold text-on-surface">
              <span>Total</span>
              <span>{formatCurrency(invoice.total, invoice.currency)}</span>
            </div>
            <div className="mt-1 flex justify-between text-sm text-on-surface-variant">
              <span>Amount due</span>
              <span>{formatCurrency(invoice.amount_due, invoice.currency)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
